import { palette, admin, transition, settingsHub, fontFamily } from '../../../styles/tokens';
import type { Urgency } from '../hooks/useTrialIntelligence';

interface SettingsProgressBarProps {
  percentage: number;
  message: string;
  subMessage: string;
  urgency: Urgency;
  canExtend: boolean;
  onExtend: () => void;
}

const URGENCY_BG: Record<Urgency, string> = {
  calm: settingsHub.urgencyCalm,
  nudge: settingsHub.urgencyNudge,
  urgent: settingsHub.urgencyUrgent,
  expired: settingsHub.urgencyExpired,
};

const URGENCY_ACCENT: Record<Urgency, string> = {
  calm: palette.accent,
  nudge: palette.accent,
  urgent: palette.amber,
  expired: palette.red,
};

export function SettingsProgressBar({
  percentage,
  message,
  subMessage,
  urgency,
  canExtend,
  onExtend,
}: SettingsProgressBarProps) {
  const bg = URGENCY_BG[urgency];
  const accent = URGENCY_ACCENT[urgency];

  return (
    <div style={{
      background: bg,
      borderRadius: admin.cardRadius,
      border: `0.5px solid ${palette.g200}`,
      padding: '16px 20px',
      marginBottom: 28,
      display: 'flex',
      alignItems: 'center',
      gap: 20,
      fontFamily,
    }}>
      {/* Percentage */}
      <div style={{
        fontSize: 20,
        fontWeight: 500,
        color: accent,
        minWidth: 48,
        textAlign: 'center',
        flexShrink: 0,
      }}>
        {percentage}%
      </div>

      {/* Message */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 14,
          fontWeight: 600,
          color: palette.g800,
          marginBottom: 2,
        }}>
          {message}
        </div>
        <div style={{
          fontSize: 12.5,
          color: palette.g500,
          lineHeight: 1.4,
        }}>
          {subMessage}
        </div>
      </div>

      {/* Progress bar */}
      <div style={{
        width: 120,
        height: 6,
        borderRadius: 3,
        background: palette.g200,
        flexShrink: 0,
        overflow: 'hidden',
      }}>
        <div style={{
          width: `${percentage}%`,
          height: '100%',
          borderRadius: 3,
          background: accent,
          transition: `width ${transition.slow}`,
        }} />
      </div>

      {/* Extend / Upgrade button */}
      {canExtend && (
        <button
          onClick={onExtend}
          style={{
            padding: '8px 16px',
            borderRadius: 8,
            border: 'none',
            background: palette.accent,
            color: palette.white,
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily,
            whiteSpace: 'nowrap',
            transition: `background ${transition.fast}`,
            flexShrink: 0,
          }}
        >
          Extend trial
        </button>
      )}

      {urgency === 'expired' && (
        <a
          href="https://crewbit.ca/pricing"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            padding: '8px 16px',
            borderRadius: 8,
            background: palette.accent,
            color: palette.white,
            fontSize: 13,
            fontWeight: 600,
            textDecoration: 'none',
            fontFamily,
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          Upgrade
        </a>
      )}
    </div>
  );
}
